import {Form, Select} from 'antd';
import {useQuery} from '@apollo/client';
import {GET_ALL_COLLECTIONS} from 'graphql/queries/collections';
import {FinancialInstrumentPicker} from 'components/financialInstrumentPicker';
import {fieldRules} from 'utils/helper';

const {Option} = Select;

/**
 * Destination selector for the single-entity announcement types.
 *
 * Instrument v2: every instrument destination submits `instrumentId`. The
 * deprecated `company` (for a stock) and `instrument` (for a bond or fund)
 * inputs are rejected at runtime on dev, so neither is offered any more.
 *
 * The announcement enum has no `SINGLE_IPO`, `SINGLE_VENTURE` or
 * `SINGLE_VENTURE_FUND`; those three ride on `SINGLE_OPPORTUNITY` with an
 * Instrument v2 id. Because one enum value covers three different pickers, the
 * caller passes the chosen `targetKind` alongside the type.
 */

/** Announcement type -> picker target, for the types that map one-to-one. */
export const ANNOUNCEMENT_INSTRUMENT_TARGETS = {
  SINGLE_STOCK: 'PUBLIC_EQUITY',
  SINGLE_BOND: 'BOND',
  SINGLE_MUTUAL_FUND: 'MUTUAL_FUND',
};

/** What a SINGLE_OPPORTUNITY announcement can point at under Instrument v2. */
export const OPPORTUNITY_TARGETS = [
  {value: 'IPO', label: 'IPO'},
  {value: 'PRIVATE_EQUITY', label: 'Startup / private equity'},
  {value: 'VENTURE_FUND', label: 'Venture fund'},
];

export const isOpportunityType = type => type === 'SINGLE_OPPORTUNITY';

export const isSingleEntityType = type =>
  type === 'SINGLE_COLLECTION' ||
  isOpportunityType(type) ||
  Boolean(ANNOUNCEMENT_INSTRUMENT_TARGETS[type]);

export const AnnouncementEntitySelector = ({
  type,
  width = 730,
  onSelect,
  // Only meaningful for SINGLE_OPPORTUNITY, where one type serves three kinds.
  opportunityKind,
  onOpportunityKindChange,
}) => {
  const isCollection = type === 'SINGLE_COLLECTION';
  const directTarget = ANNOUNCEMENT_INSTRUMENT_TARGETS[type];
  const targetKind = isOpportunityType(type) ? opportunityKind : directTarget;

  const {data: getAllCollections, loading: loadingCollections} = useQuery(
    GET_ALL_COLLECTIONS,
    {skip: !isCollection, variables: {filters: {all: true}}},
  );

  if (isCollection) {
    return (
      <Form.Item
        label="Collection"
        name="collection"
        rules={[fieldRules('Collection')]}>
        <Select
          style={{width}}
          showSearch
          optionFilterProp="label"
          loading={loadingCollections}
          allowClear
          placeholder="Select a collection"
          onChange={value => onSelect?.('collection', value)}>
          {getAllCollections?.getAllCollections?.data?.collections?.map(
            collection => (
              <Option
                key={collection.id}
                value={collection.id}
                label={collection.name}>
                {collection.name}
              </Option>
            ),
          )}
        </Select>
      </Form.Item>
    );
  }

  if (!targetKind && !isOpportunityType(type)) return null;

  return (
    <>
      {isOpportunityType(type) && (
        <Form.Item
          label="Opportunity kind"
          rules={[{required: true, message: 'Pick what this points at'}]}
          tooltip="The announcement enum has one SINGLE_OPPORTUNITY value for all three; this decides which instruments you can pick from.">
          <Select
            style={{width}}
            value={opportunityKind}
            placeholder="IPO, startup or venture fund"
            onChange={value => {
              onOpportunityKindChange?.(value);
              // The previous pick belongs to a different instrument family.
              onSelect?.('instrumentId', undefined);
            }}>
            {OPPORTUNITY_TARGETS.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item
        label="Instrument"
        name="instrumentId"
        rules={[fieldRules('Instrument')]}
        style={{maxWidth: width}}
        extra="The destination is the instrument itself — never its issuer or a campaign.">
        {/* No wrapper element here: Form.Item injects `value`/`onChange` into
            its direct child, so a <div> in between swallowed both and the
            picker never received the saved value. */}
        <FinancialInstrumentPicker
          targetKind={targetKind}
          onChange={value => onSelect?.('instrumentId', value)}
          placeholder={
            targetKind ? undefined : 'Pick an opportunity kind first'
          }
        />
      </Form.Item>
    </>
  );
};
